import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useOrganizationActivity } from "@/hooks/peo-organizations/useOrganizationActivity";
import { formatDistanceToNow } from "date-fns";
import { Clock, Users, Shield, Building2, Settings } from "lucide-react";

interface RecentActivityCardProps {
  providerId: string;
}

const getActivityIcon = (actionType: string) => {
  switch (actionType) {
    case 'role_assigned':
    case 'role_updated':
      return <Shield className="h-3 w-3" />;
    case 'team_created':
    case 'team_updated':
      return <Users className="h-3 w-3" />;
    case 'org_structure_updated':
      return <Building2 className="h-3 w-3" />;
    default:
      return <Settings className="h-3 w-3" />;
  }
};

const getActivityColor = (actionType: string) => {
  switch (actionType) {
    case 'role_assigned':
      return 'bg-blue-500';
    case 'team_created':
      return 'bg-green-500';
    case 'role_updated':
    case 'team_updated':
      return 'bg-yellow-500';
    case 'org_structure_updated':
      return 'bg-purple-500';
    default:
      return 'bg-muted-foreground';
  }
};

export const RecentActivityCard = ({ providerId }: RecentActivityCardProps) => {
  const { activities, isLoading, error } = useOrganizationActivity(providerId, 5);

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Recent Activity</CardTitle>
          <CardDescription>Latest organizational changes</CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-center text-muted-foreground text-sm">
            Failed to load recent activity
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Recent Activity</CardTitle>
        <CardDescription>Latest organizational changes</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {isLoading ? (
          <div className="space-y-3">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="flex items-center gap-3 animate-pulse">
                <div className="w-2 h-2 bg-muted rounded-full"></div>
                <div className="flex-1 space-y-1">
                  <div className="h-4 bg-muted rounded w-3/4"></div>
                  <div className="h-3 bg-muted rounded w-1/2"></div>
                </div>
              </div>
            ))}
          </div>
        ) : activities.length === 0 ? (
          <div className="flex items-center justify-center py-8 text-muted-foreground">
            <div className="text-center">
              <Clock className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">No recent activity</p>
            </div>
          </div>
        ) : (
          activities.map((activity) => (
            <div key={activity.id} className="flex items-center gap-3">
              <div className={`w-2 h-2 rounded-full ${getActivityColor(activity.action_type)}`}></div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{activity.action_description}</p>
                <div className="flex items-center gap-2 mt-1">
                  <p className="text-xs text-muted-foreground">
                    {formatDistanceToNow(new Date(activity.created_at), { addSuffix: true })}
                  </p>
                  {activity.user_name && activity.user_name !== 'System' && (
                    <Badge variant="outline" className="text-xs px-1 py-0">
                      {activity.user_name}
                    </Badge>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </CardContent>
    </Card>
  );
};